import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MuiAutocomplete, { type AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import type { AutocompleteInputChangeReason } from '@mui/material/useAutocomplete';
import { mergeSlotProps } from '@mui/material/utils';
import { useCallback, useMemo, useState } from 'react';
import { useController } from 'react-hook-form';

import { DebounceField } from '../DebounceField/DebounceField.tsx';
import { TextField } from '../TextField/TextField.tsx';

import { AutoCompleteRenderOptions } from './AutoCompleteRenderOptions.tsx';
import { getInputSlotProps } from './getInputSlotProps.tsx';
import type { RenderOptions, RHFAutocompleteProps } from './types.ts';

export const RHFAutoComplete = <
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>({
  name,
  control,
  textFieldProps,
  loading,
  debounced = false,
  onDebounceChange,
  onOptionBlur,
  renderOptions,
  onInputChange,
  slotProps,
  isOptionEqualToValue,
  value,
  onChange,
  key,
  ...rest
}: RHFAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>): JSX.Element => {
  const {
    field: { onChange: fieldOnChange, ...restFieldProps },
    fieldState: { invalid, error },
    formState: { isSubmitting },
  } = useController({
    control,
    name,
  });
  const [inputValue, setInputValue] = useState('');

  const CommonProps = useMemo(
    () => ({
      disabledItemsFocusable: true,
      limitTags: 1,
      multiple: undefined,
      popupIcon: <KeyboardArrowDownIcon />,
    }),
    [],
  );

  const mergedSlotProps = useMemo<RHFAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['slotProps']>(
    () => ({
      ...slotProps,
      chip: mergeSlotProps(slotProps?.chip, {
        'data-test': 'chip-button',
        deleteIcon: (
          <IconButton size="large">
            <CloseIcon />
          </IconButton>
        ),
      }),
    }),
    [slotProps],
  );

  const TextInputComponent = useMemo(() => (debounced ? DebounceField : TextField), [debounced]);

  const handleInputChange = useCallback(
    (e, newValue: string, reason: AutocompleteInputChangeReason) => {
      if (reason === 'reset') {
        setInputValue('');
      } else {
        setInputValue(newValue);
      }
      onInputChange?.(e, newValue, reason);
    },
    [onInputChange],
  );

  const getOptionValue = useCallback(
    (
      option: T,
      getOptionValue?: RenderOptions<T>['getOptionValue'],
      getOptionLabel?: RenderOptions<T>['getOptionValue'],
    ) => {
      if (getOptionValue) return getOptionValue(option);
      if (getOptionLabel) return getOptionLabel(option);

      return typeof option === 'string' ? option : '';
    },
    [],
  );

  const getDataTest = useCallback((option: T, dataTest?: string | ((option: T) => string)) => {
    return typeof dataTest === 'string' ? dataTest : dataTest?.(option);
  }, []);

  const hasMultiple = useMemo(() => rest.multiple === true, [rest.multiple]);

  const onOptionChange = useCallback(
    (_, value, reason) => {
      onChange ? onChange(_, value, reason) : fieldOnChange(value);
    },
    [fieldOnChange, onChange],
  );

  const onBlur = useCallback(() => {
    restFieldProps.onBlur();
    onOptionBlur?.();
    // biome-ignore lint/correctness/useExhaustiveDependencies: I'm not suer if we can remove restFieldProps from depenendency array
  }, [restFieldProps, onOptionBlur]);

  const renderOption = useCallback(
    (state, option: T, { selected }) => (
      <li {...state} key={state.key || option}>
        <AutoCompleteRenderOptions
          option={getOptionValue(option, renderOptions?.getOptionValue, rest?.getOptionLabel)}
          childrenLocation={renderOptions?.childrenLocation}
          dataTest={getDataTest(option, renderOptions?.dataTest)}
          phraseToHighlight={inputValue}
          getPaddingLevel={() => renderOptions?.getPaddingLevel?.(option)}
          tooltip={renderOptions?.tooltip}
          tooltipPlacement={renderOptions?.tooltipPlacement}
          hasMultiple={hasMultiple}
          selected={selected}
          key={state.key || option}
        >
          {renderOptions?.renderChildren?.(option)}
        </AutoCompleteRenderOptions>
      </li>
    ),
    [getDataTest, getOptionValue, hasMultiple, inputValue, renderOptions, rest?.getOptionLabel],
  );

  const renderInput = useCallback(
    ({ slotProps: inputSlotProps, ...restParams }: AutocompleteRenderInputParams) => (
      // @ts-ignore
      <TextInputComponent
        onChange={(e) => onDebounceChange?.(e)}
        {...restParams}
        {...textFieldProps}
        slotProps={getInputSlotProps({
          inputSlotProps,
          textFieldSlotProps: textFieldProps?.slotProps,
          loading,
          readOnly: rest.textFieldReadOnly,
        })}
        helperText={(Array.isArray(error) && error[0]?.message) || error?.message || textFieldProps?.helperText}
        error={invalid || textFieldProps?.error}
      />
    ),
    [TextInputComponent, error, invalid, loading, onDebounceChange, rest.textFieldReadOnly, textFieldProps],
  );

  return (
    <StyledAutocomplete
      {...restFieldProps}
      {...CommonProps}
      value={typeof value !== 'undefined' ? value : restFieldProps.value}
      onChange={onOptionChange}
      onBlur={onBlur}
      disableCloseOnSelect={hasMultiple}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderOption}
      onInputChange={handleInputChange}
      {...rest}
      slotProps={mergedSlotProps}
      renderInput={renderInput}
      disabled={isSubmitting || rest.disabled}
    />
  );
};

const StyledAutocomplete = styled(MuiAutocomplete, {
  shouldForwardProp: (prop) => prop !== 'textFieldReadOnly',
})<{
  textFieldReadOnly?: boolean;
}>`
  & .MuiAutocomplete-input {
    cursor: ${({ textFieldReadOnly }) => (textFieldReadOnly ? 'pointer' : 'auto')};
  }
` as typeof MuiAutocomplete;
